import { t } from 'i18next';

import {
  TranslationField,
  TranslationSection,
} from 'Components/structural/STranslationInterface/STranslationInterface';

import {
  ANSWER_CHAR_LIMIT,
  ERROR_TEXT_LIMIT,
  EXPLANATION_CHAR_LIMIT,
  MCQ_ANSWER_CHAR_LIMIT,
  MCQ_QUESTION_CHAR_LIMIT,
  NO_INTERACTION_TEXT_CHAR_LIMIT,
  NO_INTERACTION_TITlE_CHAR_LIMIT,
  ORDERING_CARDS_LABEL_CHAR_LIMIT,
  ORDERING_CARDS_QUESTION_CHAR_LIMIT,
  ORDERING_CARDS_TITLE_CHAR_LIMIT,
  PICTURE_SPOT_DESCRIPTION_LIMIT,
  QUESTION_CHAR_LIMIT,
} from '../GameplaysForm/gameplay.constants';
import { ContentFormValues } from './contentForm.hook';

export function handleTranslateMCQ(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'mcq') return [];

  const choices =
    content.game.choices?.map((choice, index) => ({
      label: `${t('gameplays:mcq.choice')} ${index + 1} ${choice?.correct ? `(${t('gameplays:mcq.selected')})` : ''}`,
      value: choice?.text,
      name: `game.choices[${index}].text`,
      maxCharacter: MCQ_ANSWER_CHAR_LIMIT,
      extraValues: { correct: choice?.correct },
    })) || [];

  const mcqSection: TranslationSection = {
    items: [
      {
        label: t('gameplays:question'),
        value: content.game?.question,
        name: 'question',
        maxCharacter: MCQ_QUESTION_CHAR_LIMIT,
        type: 'textarea',
      },
      ...choices,
      {
        label: t('gameplays:explanation'),
        value: content.game?.explanation,
        name: 'explanation',
        maxCharacter: EXPLANATION_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, mcqSection];
}

export function handleTranslateErrorText(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'error-text') return [];

  const errorTextSection: TranslationSection = {
    items: [
      {
        label: t('gameplays:error_text.create_correct_sentence'),
        value: content.game?.correctText,
        name: 'correctText',
        maxCharacter: ERROR_TEXT_LIMIT,
        type: 'textarea',
      },
      {
        label: t('gameplays:error_text.create_wrong_sentence'),
        value: content.game?.wrongText,
        name: 'wrongText',
        maxCharacter: ERROR_TEXT_LIMIT,
        type: 'textarea',
      },
      {
        label: t('gameplays:explanation'),
        value: content.game?.explanation,
        name: 'explanation',
        maxCharacter: EXPLANATION_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, errorTextSection];
}

export function handleTranslateOpenQuestion(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'open-question') return [];

  const openQuestionSection: TranslationSection = {
    items: [
      {
        label: t('gameplays:question'),
        value: content.game?.question,
        name: 'question',
        maxCharacter: QUESTION_CHAR_LIMIT,
        type: 'textarea',
      },
      {
        label: t('gameplays:answer'),
        value: content.game?.answer,
        name: 'answer',
        maxCharacter: ANSWER_CHAR_LIMIT,
        type: 'textarea',
      },
      {
        label: t('gameplays:explanation'),
        value: content.game?.explanation,
        name: 'explanation',
        maxCharacter: EXPLANATION_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, openQuestionSection];
}

export function handleTranslateNoInteraction(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'no-interaction') return [];

  const noInteractionSection: TranslationSection = {
    items: [
      {
        label: t('gameplays:content'),
        value: content.game?.title,
        name: 'title',
        maxCharacter: NO_INTERACTION_TITlE_CHAR_LIMIT,
        type: 'textarea',
      },
      {
        label: t('gameplays:text'),
        value: content.game?.text,
        name: 'text',
        maxCharacter: NO_INTERACTION_TEXT_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, noInteractionSection];
}

export function handleTranslatePictureSpot(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'picture-spot') return [];

  const items2 =
    content.game?.items && Array.isArray(content.game.items)
      ? content.game.items.reduce(
          (acc, item) => {
            return {
              array: [
                ...acc.array,
                {
                  label: `${t('gameplays:picture_spot.image_description')} ${acc.index + 1}`,
                  value: item?.description,
                  name: `items[${acc.index}].description`,
                  maxCharacter: PICTURE_SPOT_DESCRIPTION_LIMIT,
                  type: 'textarea' as const,
                },
                {
                  label: `${t('gameplays:picture_spot.question')} ${acc.index + 1}`,
                  value: item?.question,
                  name: `items[${acc.index}].question`,
                  maxCharacter: QUESTION_CHAR_LIMIT,
                  type: 'textarea' as const,
                },
                {
                  value: item?.x,
                  name: `items[${acc.index}].x`,
                  type: 'hidden' as const,
                },
                {
                  value: item?.y,
                  name: `items[${acc.index}].y`,
                  type: 'hidden' as const,
                },
                {
                  value: item?.r,
                  name: `items[${acc.index}].r`,
                  type: 'hidden' as const,
                },
              ],
              index: acc.index + 1,
            };
          },
          { array: [] as TranslationField[], index: 0 },
        )
      : { array: [], index: 0 };

  const pictureSpotSection: TranslationSection = {
    items: [
      ...items2.array,
      {
        label: t('gameplays:explanation'),
        value: content.game?.explanation,
        name: 'explanation',
        maxCharacter: EXPLANATION_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, pictureSpotSection];
}

export function handleTranslateOrderingCards(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'ordering-cards') return [];

  const cards =
    content.game.cards?.map((card, index) => ({
      label: `${t('gameplays:ordering_cards.card')} ${index + 1}`,
      value: card,
      name: `game.cards[${index}]`,
      maxCharacter: ORDERING_CARDS_TITLE_CHAR_LIMIT,
    })) || [];

  const orderingCardsSection: TranslationSection = {
    items: [
      {
        label: t('gameplays:ordering_cards.instruction_title'),
        value: content.game?.question,
        name: 'question',
        maxCharacter: ORDERING_CARDS_QUESTION_CHAR_LIMIT,
        type: 'textarea',
      },
      {
        label: t('gameplays:ordering_cards.top_label'),
        value: content.game?.topLabel,
        name: 'topLabel',
        maxCharacter: ORDERING_CARDS_LABEL_CHAR_LIMIT,
        type: 'input',
      },
      {
        label: t('gameplays:ordering_cards.bottom_label'),
        value: content.game?.bottomLabel,
        name: 'bottomLabel',
        maxCharacter: ORDERING_CARDS_LABEL_CHAR_LIMIT,
        type: 'input',
      },
      ...cards,
      {
        label: t('gameplays:explanation'),
        value: content.game?.explanation,
        name: 'explanation',
        maxCharacter: EXPLANATION_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, orderingCardsSection];
}

export function handleTranslateSwipingCards(content: ContentFormValues, metadataSection: TranslationSection) {
  if (!content || !content.game || !content.game.type || content.game.type !== 'swiping-cards') return [];

  const cards =
    content.game.cards?.map((card, index) => ({
      label: `${t('gameplays:swiping_cards.card')} ${index + 1} `,
      value: card?.text,
      name: `game.cards[${index}].text`,
      maxCharacter: QUESTION_CHAR_LIMIT,
      extraValues: { answer: card?.answer },
    })) || [];

  const zones =
    content.game.zones?.map((zone, index) => ({
      label: `${t('gameplays:swiping_cards.zone')} ${index + 1} `,
      value: zone?.text,
      name: `game.zones[${index}].text`,
      maxCharacter: QUESTION_CHAR_LIMIT,
      extraValues: { position: zone?.position },
    })) || [];

  const swipingCardsSection: TranslationSection = {
    items: [
      {
        label: t('gameplays:question'),
        value: content.game?.question,
        name: 'question',
        maxCharacter: QUESTION_CHAR_LIMIT,
      },
      ...zones,
      ...cards,
      {
        label: t('gameplays:explanation'),
        value: content.game?.explanation,
        name: 'explanation',
        maxCharacter: EXPLANATION_CHAR_LIMIT,
        type: 'textarea',
      },
    ],
  };

  return [metadataSection, swipingCardsSection];
}
